import { Component, OnInit, ChangeDetectionStrategy} from '@angular/core';
import { ConditionMonitoringService, ICMEquipmentAlerts, IDisplayVehicle } from '../../condition-monitoring.service';
import { map, tap } from 'rxjs/operators';
import { XlatPipe } from '../../../../../amt/i18n/xlat-pipe';
import { combineLatest } from 'rxjs';
import { Inject } from '@angular/core';
import { ConditionMonitoringBaseComponent, ICON_HEIGHT, ICON_WIDTH } from '../condition-monitoring-base.component';
import { INextInspectionDateGroup } from '@Models/condition-monitoring/INextInspectionDateGroup';
import { NextInspectionDateGroup } from '@Models/condition-monitoring/NextInspectionDateGroup';
import { IConditonMonitoringTabs } from '@Models/condition-monitoring/IConditionMonitoringTabs';
import { CM_RENDERER_CONFIG, rendererConfigFactory, RendererConfigProvider } from '../renderer-config-provider/cm-renderer-config.provider';
 
@Component({
    selector: 'tread-wear',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './../condition-monitoring-base.component.html', //./tread-wear.component.html
    styleUrls: ['./tread-wear.component.scss'],
    providers: [
        { provide: 'desiredWidth', useValue: ICON_WIDTH },
        { provide: 'desiredHeight', useValue: ICON_HEIGHT },
        { provide: CM_RENDERER_CONFIG, useFactory: rendererConfigFactory },
        ConditionMonitoringService]
})
export class ConditionMonitoringTreadWearComponent extends ConditionMonitoringBaseComponent implements OnInit {
    // TODO: xlat this stuff
    _displayBy = [{ text: "Tread Wear Thresholds", value: 2 }, { text: "Tread Matching", value: 3 }];
    displayBySelectedItem = this._displayBy.find(elem => elem.value === 2);
    
    protected override nextInspectionDateGroups: INextInspectionDateGroup[] = [
        new NextInspectionDateGroup(this.xlatPipe.transform('conditionmonitoring.inspected_24h'), undefined, 1),
        new NextInspectionDateGroup(this.xlatPipe.transform('conditionmonitoring.inspected_48h'), 1, 2),
        new NextInspectionDateGroup(this.xlatPipe.transform('conditionmonitoring.inspected_7d'), 2, 7),
        new NextInspectionDateGroup(this.xlatPipe.transform('conditionmonitoring.inspected_14d'), 7, 14),
        new NextInspectionDateGroup(this.xlatPipe.transform('conditionmonitoring.inspected_30d'), 14, 30),
        new NextInspectionDateGroup(this.xlatPipe.transform('conditionmonitoring.inspected_non'), 30, undefined),
    ];

    protected override detailsTabs: IConditonMonitoringTabs[] = [
        { 
            title: "conditionmonitoring.tab_conditions_details",
            selected: false,
            type: 'conditionDetails',
        },
        { 
            title: "conditionmonitoring.treadwear",
            selected: true,
            type: 'treadWear',
        },
        {
            title: "conditionmonitoring.visual_inspection_tab",
            selected: false,
            type: 'visualInspection',
        },
    ];

    constructor(
        protected monSvc: ConditionMonitoringService,
        protected xlatPipe: XlatPipe,
        @Inject('WindowFactory') protected WindowFactory: any,
        @Inject('CM_RENDERER_CONFIG') protected configRendererProvider: RendererConfigProvider,
    ) {
        super(monSvc, xlatPipe, WindowFactory);
        this.setTitle("conditionmonitoring.treadwear");
    }

    public groupedVehicles$: any;

    ngOnInit(): void {
        super.ngOnInit();
        this.setRendererConfig();
        this.updateData();
    }

    public onUpdateButtonClick(): void {
        super.toggleFilter(true);
        this.setRendererConfig();
        this.updateData();
    }

    private updateData(): void {
        this.setProcessing();

        this.groupedVehicles$ = combineLatest([
            this.monSvc.getVehicles$, 
            this.monSvc.currentAlerts$,
        ]).pipe(
            map(v => this.sortVehicles(v, this.displayBySelectedItem.value)),
            map(v => this.groupBy(v, this.sortBySelectedItem.value)),
            tap(x => this.isProcessing = false)
        );
    }

    /// Used to configure wheel colours and legend
    private setRendererConfig() {
        this.rendererConfig = this.configRendererProvider(this.displayBySelectedItem?.value || -1);
    }

    private sortVehicles(data: [IDisplayVehicle[], [string, ICMEquipmentAlerts[]][]], displayBy: number): IDisplayVehicle[] {
        const vehicles = data[0];
        const issues = data[1];

        for (let issue of issues) {
            const broombroom = vehicles.find((veh) => { return veh.equipmentId === issue[0] })
            const issuesList: ICMEquipmentAlerts[] = [];

            //Per vehicle NOT issue
            let axles = broombroom.spec?.axles;
            let posLabels = [].concat(...axles);

            for (let item of issue[1]) {
                if (this.displayBySelectedItem.value === 0 && item.cause !== 'MATCHINGLIMITEXCEEDED') //Tread Matching
                    continue;

                item.issue = this.getIssue(item);
                item.posIdx = posLabels.indexOf(item.posLabel);
                item.issueText = `Pos ${item.posLabel}: ` + this.xlatPipe.transform(`conditionmonitoring.${this.rendererConfig?.issues[item.issue]}`);
                //ensure each position only has one alert
                const dupIdx = issuesList.findIndex(i => i.posLabel === item.posLabel);
                if (dupIdx < 0) {
                    issuesList.push(item);
                } else if (issuesList[dupIdx].issue < item.issue) {
                    issuesList[dupIdx] = item;
                }
            }
            broombroom.issues = issuesList;
            broombroom.rawIssues = issue[1];
            const issueText = issuesList.sort((a, b) => parseInt(a.posLabel) - parseInt(b.posLabel)).map(m => m.issueText).join("<br />") || this.xlatPipe.transform("conditionmonitoring.NoIssue");//Sort the array here for now. PosLabels were in DESC order (from the database - may have been because of date ordering - leave the sort in anyway?)
            broombroom.altText = this.xlatPipe.transform("conditionmonitoring.treadwear") + ":<br />" + issueText;
        }

        return vehicles;
    }

    
    private getIssue(alert: ICMEquipmentAlerts) : number {
        const config = this.rendererConfig?.issues || [];
        switch (this.displayBySelectedItem?.value) {
            case 0: //Tread Matching
                return config.findIndex(issue => issue === 'tw_tread_matching_issue');
            case 1: //Tread Wear Thresholds
            default:
                return this.monSvc.getAlertIssue(alert, config);
        }
    }
}
